import React, { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import { useHistory } from 'react-router';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { TextAreaField } from 'ksnm-common-ui/lib/Form/TextAreaField';

import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { controlGroupsStore } from '@stores/dictionaryStores/controlGroupsStore/controlGroupsStore';
import { controlEditStore } from '@stores/dictionaryStores/controlsStore/controlEditStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import type { ReportGroupsDto } from '@src/api';

import { NEW_ENTITY_ID } from '../../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../../FormWithActions';

import styles from './Form.module.scss';

const renderName = (value: ReportGroupsDto) => `${value.code} - ${value.name}`;

export const Form = observer(() => {
  const { isEditMode, isNew } = controlEditStore;
  const { reportGroups } = reportGroupsStore;
  const { controlGroups } = controlGroupsStore;

  const { push } = useHistory();

  const cmControlVersionPermission = usePermission('CmControlVersion');

  useEffect(() => {
    fetchReferences(['reportGroups', 'controlGroups', 'controls']);
  }, []);

  const {
    input: { value: repGroupCode },
  } = useField('repGroupCode');

  const {
    input: { value: ctrlCode },
  } = useField('ctrlCode');

  const controlGroupsItems = useMemo(
    () =>
      controlGroups.map((group) => ({
        id: group.ctrlGroupCode,
        name: `${group.ctrlGroupCode} - ${group.ctrlGroupName}`,
      })),
    [controlGroups],
  );

  const onCreateControl = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROL_VERSIONS}/${NEW_ENTITY_ID}`, { repGroupCode, ctrlCode });
  }, [push, repGroupCode, ctrlCode]);

  const onGoControls = useCallback(() => {
    push(RoutePath.DICTIONARY_CONTROL_VERSIONS, { ctrlCode });
  }, [ctrlCode, push]);

  const goToControls = useCallback(() => push(RoutePath.DICTIONARY_CONTROLS), [push]);

  return (
    <FormWithActions
      store={controlEditStore}
      goBackLink={goToControls}
      actions={
        <div className={styles.buttons}>
          <Button onClick={onCreateControl} disabled={isNew || !cmControlVersionPermission.CREATE}>
            Создать версию контроля
          </Button>
          <Button onClick={onGoControls}>Перейти к версиям контролей</Button>
        </div>
      }
    >
      <>
        <SelectField
          items={reportGroups}
          name="repGroupCode"
          label="Код группы отчетных форм"
          disabled={!isNew}
          size="small"
          groupStyle={!isEditMode}
          renderName={renderName}
          allowClear
          required
        />
        <SelectField
          items={controlGroupsItems}
          name="ctrlGroupCode"
          label="Код группы контроля"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          allowClear
          required
        />
        <TextField
          name="ctrlCode"
          label="Технический код контроля"
          disabled={!isNew}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={15}
        />
        <TextField
          name="ctrlExt"
          label="Код контроля"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={100}
        />
        <TextAreaField
          name="ctrlName"
          label="Наименование контроля"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={1000}
          autoSize
        />
      </>
    </FormWithActions>
  );
});
